import { create } from "zustand";
import { FONT_PRESET_OPTIONS, type FontPreset } from "./fonts";
import { processFile, initializeModel, getModelInfo } from "./process";

interface AppError {
  message: string;
}

export interface ImageFile {
  file: File;
  processedFile?: File;
}

interface ImageState {
  isLoading: boolean;
  error: AppError | null;
  isWebGPU: boolean;
  isIOS: boolean;
  currentModel: "briaai/RMBG-1.4" | "Xenova/modnet";
  isModelSwitching: boolean;
  image: ImageFile | null;
  text: string;
  fontStyle: FontPreset;

  setIsLoading: (isLoading: boolean) => void;
  setError: (error: AppError | null) => void;
  setIsWebGPU: (isWebGPU: boolean) => void;
  setIsIOS: (isIOS: boolean) => void;
  setCurrentModel: (model: "briaai/RMBG-1.4" | "Xenova/modnet") => void;
  setIsModelSwitching: (isModelSwitching: boolean) => void;
  setImage: (image: ImageFile | null) => void;
  setText: (text: string) => void;
  setFontStyle: (fontStyle: FontPreset) => void;
  handleModelChange: (
    model: "briaai/RMBG-1.4" | "Xenova/modnet"
  ) => Promise<void>;
  processImage: (file: File) => Promise<void>;
}

export const useAppStore = create<ImageState>((set, get) => ({
  isLoading: true,
  error: null,
  isWebGPU: false,
  isIOS: false,
  currentModel: "briaai/RMBG-1.4",
  isModelSwitching: false,
  image: null,
  text: "ImageType",
  fontStyle: FONT_PRESET_OPTIONS[0],

  setIsLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  setIsWebGPU: (isWebGPU) => set({ isWebGPU }),
  setIsIOS: (isIOS) => set({ isIOS }),
  setCurrentModel: (currentModel) => set({ currentModel }),
  setIsModelSwitching: (isModelSwitching) => set({ isModelSwitching }),
  setImage: (image) => set({ image }),
  setText: (text) => set({ text }),
  setFontStyle: (fontStyle) => set({ fontStyle }),

  handleModelChange: async (newModel) => {
    const { setIsModelSwitching, setError, setCurrentModel } = get();
    setIsModelSwitching(true);
    setError(null);
    try {
      const initialized = await initializeModel(newModel);
      if (!initialized) {
        throw new Error("Failed to initialize new model");
      }
      setCurrentModel(newModel);
    } catch (err) {
      if (err instanceof Error && err.message.includes("Falling back")) {
        setCurrentModel("briaai/RMBG-1.4");
      } else {
        setError({
          message:
            err instanceof Error ? err.message : "Failed to switch models",
        });
      }
    } finally {
      setIsModelSwitching(false);
    }
  },

  processImage: async (file) => {
    const { setImage, setIsLoading, setError, setIsWebGPU, image } = get();
    const newImage = {
      file,
      processedFile: undefined,
    };

    setImage(newImage);

    if (!image) {
      setIsLoading(true);
      setError(null);
      try {
        const initialized = await initializeModel();
        if (!initialized) {
          throw new Error("Failed to initialize background removal model");
        }
        const { isWebGPUSupported } = getModelInfo();
        setIsWebGPU(isWebGPUSupported);
      } catch (err) {
        setError({
          message:
            err instanceof Error ? err.message : "An unknown error occurred",
        });
        setImage(null);
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
    }

    try {
      const result = await processFile(file);
      if (result) {
        setImage({ ...(image || newImage), processedFile: result });
      }
    } catch (error) {
      console.error("Error processing image:", error);
    }
  },
}));
